import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GeoJsonLayer, TextLayer } from '@deck.gl/layers';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { WebMercatorViewport } from '@deck.gl/core';
import { Map, useControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import bbox from '@turf/bbox';
import { center, points } from '@turf/turf';
import { hexToRgb } from '../../../../constants/color';
import useMap from '../../../../hooks/Map';
import { useTheme } from '@mui/material/styles';
import { formatAddress } from '../../../../utils/address';

function DeckGLOverlay(props) {
  const overlay = useControl(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}

const ViewZoneMapModal = ({ open, onClose, currentZoneId, allZones, currentWarehouse }) => {
  const theme = useTheme();
  const [viewport, setViewport] = useState({
    width: '100%',
    height: 600,
    latitude: 37.7577,
    longitude: -122.4376,
    zoom: 8,
  });

  const { toCoordinates } = useMap();
  const mapStyle = `${process.env.REACT_APP_MAP_TILER_URI}?key=${process.env.REACT_APP_MAP_TILER_KEY}`;

  const currentZone = allZones?.find((z) => z.id === currentZoneId);

  useEffect(() => {
    if (open && allZones && allZones.length > 0) {
      updateViewport();
    }
  }, [open, allZones]);

  const updateViewport = () => {
    if (!currentZone) return;

    const currentZoneCoords = toCoordinates(currentZone.boundary);

    // Determine which coordinates to use for viewport calculation
    let coordsToUse;

    if (!currentZoneCoords || currentZoneCoords.length === 0) {
      // If current zone has no boundary, collect all valid coordinates from other zones
      const allCoords = allZones
        .map((zone) => toCoordinates(zone.boundary))
        .filter((coords) => coords && coords.length > 0)
        .flat();

      if (allCoords.length === 0) return;
      coordsToUse = allCoords;
    } else {
      // Use current zone coordinates
      coordsToUse = currentZoneCoords;
    }

    // Calculate bounds and update viewport
    const bounds = bbox({
      type: 'MultiPoint',
      coordinates: coordsToUse,
    });

    // Fit viewport to current zone bounds with generous padding
    const { longitude, latitude, zoom } = new WebMercatorViewport({
      width: 800,
      height: 600,
    }).fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ],
      { padding: 80 }, // Increased padding to ensure zone is not cut off
    );

    setViewport((prev) => ({
      ...prev,
      longitude,
      latitude,
      zoom,
    }));
  };

  const createLayers = () => {
    if (!allZones || allZones.length === 0) return [];

    const features = allZones
      .map((zone, index) => {
        const coords = toCoordinates(zone.boundary);
        if (!coords || coords.length === 0) return null;

        const p = points(coords);
        const c = center(p);
        const isCurrentZone = zone.id === currentZoneId;

        // Get main color for current zone
        const mainColor = isCurrentZone ? theme.palette.primary.main : null;

        return {
          feature: {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [coords],
            },
            properties: {
              color: mainColor,
              isCurrentZone,
            },
          },
          textData: {
            text: zone.name || zone.labels?.[0] || zone.id,
            position: c.geometry.coordinates,
            color: '#000000',
          },
        };
      })
      .filter(Boolean);

    const geoJsonLayer = new GeoJsonLayer({
      id: 'zones-layer',
      data: {
        type: 'FeatureCollection',
        features: features.map((f) => f.feature),
      },
      pickable: false,
      stroked: true,
      filled: true,
      extruded: false, // Keep as 2D to prevent z-fighting with text layer
      lineWidthScale: 20,
      lineWidthMinPixels: 2,
      getFillColor: (d) => {
        if (d.properties.isCurrentZone) {
          // Current zone: Main color with low opacity
          const rgb = hexToRgb(d.properties.color);
          return [...rgb, 100];
        } else {
          // Other zones: Gray with low opacity
          return [128, 128, 128, 70];
        }
      },
    });

    const textLayer = new TextLayer({
      id: 'text-layer',
      data: features.map((f) => f.textData),
      pickable: false,
      getPosition: (d) => d.position,
      getText: (d) => d.text,
      getColor: (d) => hexToRgb(d.color),
      maxWidth: 400,
      getSize: 12,
      getAngle: 0,
      getTextAnchor: 'middle',
      getAlignmentBaseline: 'center',
    });

    return [geoJsonLayer, textLayer];
  };

  const layers = useMemo(() => {
    if (open && currentZoneId) {
      const layers = createLayers();
      return layers;
    }
    return [];
  }, [allZones, currentZoneId, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Zone Map
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ height: 600, width: '100%', position: 'relative' }}>
          <Map {...viewport} mapStyle={mapStyle} onMove={(e) => setViewport(e.viewState)} style={{ width: '100%', height: '100%' }}>
            <DeckGLOverlay layers={layers} />
          </Map>
        </Box>

        {/* Current Zone Info */}
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'start', pt: 2, flexWrap: 'nowrap' }}>
          <Stack direction="column" sx={{ gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {currentZone?.name}
            </Typography>
            <Typography variant="caption">
              [{currentWarehouse?.id}] {currentWarehouse?.alias} @ {formatAddress(currentWarehouse?.address)}
            </Typography>
          </Stack>

          <Stack direction="column" sx={{ gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              LAT
            </Typography>
            <Typography variant="caption">{currentWarehouse?.address?.lat}</Typography>
          </Stack>

          <Stack direction="column" sx={{ gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              LONG
            </Typography>
            <Typography variant="caption">{currentWarehouse?.address?.lng}</Typography>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ViewZoneMapModal;
